import { REHYDRATE } from 'redux-persist/lib/constants';
import {
  CAM_VIEW_CHANGED,
  SET_CAM_BROWSER_CAM,
  LOGOUT_USER,
  EXPIRE_SESSION,
  SET_CAM_GROUP_SINGLE,
  SET_CAM_GROUP_QUAD
} from '../actions/types';
import { getDefaultConf } from '../actions/ConfButtonsActions';

// Map a conf (grid layout) to the default camera-view range that fills it.
// Single-cam stays cam_1; multi-cam grids use cam_1-N to match the cell count.
// Used to keep conf <-> currentCamView aligned so the live grid never
// renders 3 columns with only 1 camera (the rest go black). Also handles
// the case where conf is preserved on logout but currentCamView resets.
const CAM_VIEW_FOR_CONF = {
  'conf-1': 'cam_1',
  'conf-2': 'cam_1-2',
  'conf-4': 'cam_1-4',
  'conf-6': 'cam_1-6',
  'conf-8': 'cam_1-8',
  'conf-9': 'cam_1-9',
  'conf-12': 'cam_1-12',
  'conf-16': 'cam_1-16'
};

export const getCamViewForConf = (conf) => CAM_VIEW_FOR_CONF[conf] || 'cam_1-4';

const loadInitialCamView = () => {
  try {
    const key = `dview_conf_${window.location.hostname}`;
    const saved = localStorage.getItem(key);
    if (saved && CAM_VIEW_FOR_CONF[saved]) {
      return CAM_VIEW_FOR_CONF[saved];
    }
  } catch (e) {
    // localStorage unavailable (SSR/test) — fall through
  }
  // No stored conf → match ConfButtonsReducer's viewport-aware default
  // (phone → conf-2 → cam_1-2; tablet/desktop → conf-4 → cam_1-4).
  return getCamViewForConf(getDefaultConf());
};

const INITIAL_STATE = {
  currentCamView: loadInitialCamView(),
  camBrowserCam: '01',
  autoScrollEnabled: false,
  currentCamGroupSingle: 1,
  currentCamGroupQuad: 1
};

export default function camButtonsReducer(state = INITIAL_STATE, action) {
  switch ( action.type ) {
      case REHYDRATE: {
        // Stale persisted state can have currentCamView mismatched with conf
        // (e.g. conf='conf-9' + cam_1 from a logout that reset cam but not conf).
        // Realign on rehydrate ONLY when the persisted set is the wrong SIZE for the
        // conf; a valid non-first set (e.g. reload while on bank 3 of a conf-2 view =
        // cam_5-6, size 2) is KEPT so a refresh doesn't snap Live/playback back to bank 1
        // (#86bb7phhh — the set is shared and should survive a reload).
        const rehydratedCam = action.payload && action.payload.camera;
        const rehydratedConfSlice = action.payload && action.payload.config;
        const conf = (rehydratedConfSlice && rehydratedConfSlice.conf) || getDefaultConf();
        const defaultCamView = getCamViewForConf(conf); // the conf's first bank ('cam_1-N')
        const setSize = (cv) => {
          const p = String(cv || '').replace('cam_', '').split('-');
          const a = parseInt(p[0], 10);
          const b = p.length > 1 ? parseInt(p[1], 10) : a;
          return (Number.isFinite(a) && Number.isFinite(b)) ? (b - a + 1) : 0;
        };
        const persisted = rehydratedCam && rehydratedCam.currentCamView;
        const aligned = (persisted && setSize(persisted) === setSize(defaultCamView)) ? persisted : defaultCamView;
        return {
          ...state,
          ...(rehydratedCam || {}),
          currentCamView: aligned
        };
      }
      case CAM_VIEW_CHANGED:
        return {
            ...state,
            currentCamView: action.payload
        }
      case SET_CAM_BROWSER_CAM:
        let cam = action.payload.toString();
        if(action.payload < 10){
          cam = '0' + action.payload
        }
        return {
          ...state,
          camBrowserCam:  cam
        }
      case SET_CAM_GROUP_SINGLE:
        let group = state.currentCamGroupSingle;
        if(action.payload === 'inc') {
          group = group + 1
        } else if (action.payload === 'dec'){
          group = group - 1
        } else {
          group = action.payload
        }

        return {
            ...state,
            currentCamGroupSingle: group
        }
      case SET_CAM_GROUP_QUAD:
        let groupQ = state.currentCamGroupQuad;
        if(action.payload === 'inc') {
          groupQ = groupQ + 1
        } else if (action.payload === 'dec'){
          groupQ = groupQ - 1
        } else {
          groupQ = action.payload
        }

        return {
            ...state,
            currentCamGroupQuad: groupQ
        }
      case LOGOUT_USER:
      case EXPIRE_SESSION:
        // currentCamView resets to match the preserved conf so the grid
        // doesn't render mismatched (e.g. conf-9 layout with cam_1 data).
        return {
            ...state,
            currentCamView: loadInitialCamView(),
            camBrowserCam: '01',
            currentCamGroupSingle: 1,
            currentCamGroupQuad: 1,
            autoScrollEnabled: false
        }
      default:
          return state;
  };
};
